<template>
  <loader v-if="form === null" />
  <div v-else class="leading-normal">
    <div class="flex -mx-2 mb-3">
      <div class="flex-1 px-2">
        <h1 class="mb-1 text-2xl">{{ form.month | month }} {{ form.year }} Management Service Fee Submission</h1>
        <p class="mb-b">{{ selectedOffice.name }}</p>
      </div>
      <div class="px-2">
        <button @click="$emit('change', null)">
          <i class="fas fa-arrow-left"></i> Back
        </button>
      </div>
    </div>

    <div class="text-sm text-grey-dark mb-6">
      <p class="mb-1">Certain information in this report has been pre-populated by external sources and these fields have been greyed out. Once all the reports have been submitted we will verify the data and notify you if there have been any errors in the calculations. If you are reporting for just sales or lettings please enter 0 in the boxes for the options you are not reporting on.</p>
      <p>All fields require a numeric value.</p>
    </div>

    <panel class="pt-6 mb-6 bg-yellow-lightest border border-yellow-dark" v-if="form.status_id === 2">
      <h2 class="font-bold text-sm mb-3">Report Submitted</h2>
      <p>This report has been submitted and will be processed shortly.</p>
    </panel>
    <panel class="pt-6 mb-6 bg-yellow-lightest border border-yellow-dark" v-else-if="form.status_id === 3">
      <h2 class="font-bold text-sm mb-3">Report Complete</h2>
      <p>This report has been submitted and processed.</p>
      <div class="flex justify-end">
        <button v-if="form.invoice_url" @click="openUrl(form.invoice_url)" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow mr-3">
          <i class="fas fa-download"></i> Download Invoice
        </button>
        <button v-if="form.report_url" @click="openUrl(form.report_url)" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow">
          <i class="fas fa-download"></i> Download Summary
        </button>
      </div>
    </panel>

    <div class="flex sticky pin-t z-10 bg-grey-lighter border-b py-3">
      <button v-for="(tab, i) in filteredTabs" :key="i" class="rounded mr-2 px-6 py-3 relative text-sm" :class="i === selectedTab ? 'bg-tertiary border-tertiary text-white active' : 'bg-white'" @click="selectedTab = i">
        {{ tab.name }}
      </button>
    </div>

    <div v-if="filteredTabs[selectedTab].type === 'form'" class="flex flex-wrap -mx-2">
      <div class="w-full md:w-1/2 px-2" v-for="(col, c) in filteredTabs[selectedTab].columns" :key="c">
        <panel class="mt-6 pt-6" v-for="(panel, p) in col" :key="p">
          <h2 class="text-lg mb-6">{{ panel.name }}</h2>
          <div class="pb-6 mb-6 border-b flex flex-col" v-for="field in panel.fields" :key="field.name">
            <p v-if="field.hint" class="mb-3 text-sm">{{ field.hint }}</p>
            <label :for="field.name" class="font-bold">{{ field.label }}</label>
            <div class="flex mt-2" v-if="field.type === 'currency'">
              <div class="border-r w-10 h-10 flex items-center justify-center" :class="errorBag.get(field.name) ? 'bg-red-dark text-white border-red-dark' : (form[field.name] === '' || form[field.name] === null ? 'bg-yellow-dark text-white border-yellow-dark' : 'bg-grey-light')">
                <i class="fas fa-pound-sign fa-fw"></i>
              </div>
              <input
                type="text"
                class="block flex-grow w-full p-2 h-10 border text-sm"
                :class="errorBag.get(field.name) ? 'bg-red-lighter border-red-dark hover:border-red focus:bg-red-lightest' : (form[field.name] === '' || form[field.name] === null ? 'bg-yellow-lighter border-yellow-dark hover:border-yellow focus:bg-yellow-lightest' : (form.status_id > 1 ? 'bg-grey-light cursor-not-allowed' : 'bg-grey-lighter hover:border-grey focus:border-grey-dark'))"
                v-model="form[field.name]"
                :disabled="form.status_id > 1"
                @change="currencyOnly(field.name)" />
            </div>
            <input v-else
              type="number"
              min="0"
              class="block w-full border p-2 mt-2 h-10 text-sm"
              :class="errorBag.get(field.name) ? 'bg-red-lighter border-red-dark hover:border-red focus:bg-red-lightest' : (form[field.name] === '' || form[field.name] === null ? 'bg-yellow-lighter border-yellow-dark hover:border-yellow focus:bg-yellow-lightest' : (form.status_id > 1 ? 'bg-grey-light cursor-not-allowed' : 'bg-grey-lighter hover:border-grey focus:border-grey-dark'))"
              v-model="form[field.name]"
             :disabled="form.status_id > 1"
              @change="numbersOnly(field.name)" />
            <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get(field.name)">
              <i class="fas fa-exclamation-triangle"></i>
              {{ errorBag.get(field.name) }}
            </small>
          </div>
        </panel>
      </div>
    </div>

    <div v-else-if="filteredTabs[selectedTab].type === 'billable_items'">
      <panel class="mt-5 pt-6">
        <div v-if="billableItems.length === 0">
          <i class="fas fa-info-circle"></i> There are no billable items for this period.
        </div>
        <div v-else class="w-full max-w-full overflow-x-auto overflow-y-hidden">
          <table class="table-auto text-center w-full">
            <thead class="uppercase text-xs">
              <tr>
                <th class="text-left p-2 whitespace-no-wrap">Product</th>
                <th class="text-left p-2 whitespace-no-wrap">Description</th>
                <th class="text-left p-2 whitespace-no-wrap">Quantity</th>
                <th class="text-left p-2 whitespace-no-wrap">Unit Cost</th>
                <th class="text-left p-2 whitespace-no-wrap">Net Total</th>
                <th class="text-left p-2 whitespace-no-wrap">Tax</th>
                <th class="text-left p-2 whitespace-no-wrap">Line Total</th>
              </tr>
            </thead>
            <tbody class="text-sm">
              <tr v-for="item in billableItems" :key="item.id">
                <td class="text-left p-2">{{ item.billable_item.name }}</td>
                <td class="text-left p-2">{{ item.description }}</td>
                <td class="text-left p-2">{{ item.pivot.quantity | number }}</td>
                <td class="text-left p-2">{{ parseFloat(item.pivot.unit_cost) | gbp }}</td>
                <td class="text-left p-2">{{ (item.pivot.unit_cost * item.pivot.quantity) | gbp }}</td>
                <td class="text-left p-2">{{ parseFloat(item.pivot.tax) }}%</td>
                <td class="text-left p-2">{{ (item.pivot.unit_cost * item.pivot.quantity) * ((item.pivot.tax / 100) + 1) | gbp }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </panel>
    </div>

    <div v-else-if="filteredTabs[selectedTab].type === 'staff'">
      <h2 class="text-lg mt-6">Staff List</h2>
      <p class="mb-6 text-sm">Please see below for the current list of your staff members that we have on record. If any changes need to be made please email <a href="mailto:support@propertyfranchise.co.uk">support@propertyfranchise.co.uk</a>.</p>

      <panel class="mt-5 pt-6">
        <div v-if="users.length === 0">
          <i class="fas fa-info-circle"></i> There are no users assigned to this office.
        </div>
        <div v-else class="w-full max-w-full overflow-x-auto overflow-y-hidden">
          <table class="table-auto text-center w-full">
            <thead class="uppercase text-xs">
              <tr>
                <th class="text-left p-2 whitespace-no-wrap">First Name</th>
                <th class="text-left p-2 whitespace-no-wrap">Surname</th>
                <th class="text-left p-2 whitespace-no-wrap">Job Title</th>
                <th class="text-left p-2 whitespace-no-wrap">Last Login</th>
              </tr>
            </thead>
            <tbody class="text-sm">
              <tr v-for="user in users" :key="user.id">
                <td class="text-left p-2">{{ user.first_name }}</td>
                <td class="text-left p-2">{{ user.surname }}</td>
                <td class="text-left p-2">{{ user.position }}</td>
                <td class="text-left p-2" v-if="user.last_login">{{ user.last_login | datetime }}</td>
                <td class="text-left p-2" v-else>Never</td>
              </tr>
            </tbody>
          </table>
        </div>
      </panel>
    </div>

    <div v-else-if="filteredTabs[selectedTab].type === 'totals'" class="flex flex-wrap -mx-2">
      <div class="w-full md:w-1/2 px-2">
        <panel class="mt-6 pt-6" v-for="(panel, p) in totalsTab" :key="p">
          <h2 class="text-lg mb-6">{{ panel.name }}</h2>
          <div class="pb-6 mb-6 border-b flex flex-col" v-for="(field, i) in panel.fields" :key="i">
            <label class="font-bold">{{ field.label }}</label>
            <div class="flex mt-2">
              <div class="border-r bg-grey-light w-10 h-10 flex items-center justify-center">
                <i class="fas fa-pound-sign fa-fw"></i>
              </div>
              <div class="block flex-grow w-full bg-grey-lighter border p-2 h-10 text-sm">
                {{ field.value }}
              </div>
            </div>
          </div>
        </panel>
      </div>

      <div class="w-full md:w-1/2 px-2">
        <panel class="mt-6 pt-6">
          <h2 class="text-lg mb-6">Bank Details</h2>
          <p>{{ officeInfo.network.bacs_bank_name }}</p>
          <p>Account Name: {{ officeInfo.network.bacs_account_name }}</p>
          <p>Sort Code: {{ officeInfo.network.bacs_sort_code }}</p>
          <p>Account Number: {{ officeInfo.network.bacs_account_number }}</p>
        </panel>
        <panel class="mt-6 pt-6">
          <h2 class="text-lg mb-6">VAT Details</h2>
          <p>{{ officeInfo.network.compliance_reg_name }} registered in {{ officeInfo.network.compliance_reg_place }}. No: {{ officeInfo.network.compliance_reg_number }}</p>
          <p>Registered Office: {{ officeInfo.network.compliance_reg_address }}</p>
          <p>VAT No: {{ officeInfo.network.compliance_vat_number }}</p>
        </panel>
        <panel class="mt-6 pt-6">
          <h2 class="text-lg mb-6">Report and Email</h2>
          <div class="flex justify-end items-center">
            <span class="text-right text-sm">
              <i class="fas" :class="completedFields === totalFields ? 'fa-check-circle text-green-dark' : 'fa-exclamation-triangle text-yellow-dark'"></i>
              {{ completedFields }}/{{ totalFields }} fields completed
            </span>
            <button
              class="rounded px-6 py-3 text-sm ml-3"
              @click="save"
              :class="completedFields === totalFields ? 'bg-green-dark text-white' : 'bg-grey-light text-grey cursor-not-allowed'"
              :disabled="completedFields !== totalFields">
              <loader v-if="saving" fill-color="white" />
              <span v-else>{{ [0,1].includes(form.status_id) ? 'Submit' : 'Update non-financial information' }}</span>
            </button>
          </div>
        </panel>
      </div>
    </div>

    <div class="flex my-6">
      <div class="w-1/3 flex justify-start">
        <button v-if="selectedTab > 0" @click="selectedTab--" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow">
          <i class="fas fa-arrow-left"></i> Previous
        </button>
      </div>

      <div class="w-1/3 flex justify-center">
        <button @click="save" :disabled="saving" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow">
          <loader v-if="saving" />
          <span v-else>{{ [0,1].includes(form.status_id) ? (completedFields === totalFields ? 'Submit' : 'Save') : 'Save non-financial information' }}</span>
        </button>
      </div>

      <div class="w-1/3 flex justify-end">
        <button v-if="selectedTab < filteredTabs.length - 1" @click="selectedTab++" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow">
          Next <i class="fas fa-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import api, { v2Api } from '@/http'
import ErrorBag from '@/ErrorBag'
import Loader from '../../components/Loader.vue'
import MathUtils from '../../MathUtils'

export default {
  components: { Loader },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      form: null,
      users: [],
      billableItems: [],
      officeInfo: null,
      selectedTab: 0,
      saving: false,
      tabs: [
        {
          name: 'Lettings',
          type: 'form',
          columns: [
            [
              {
                name: 'Income Details',
                fields: [
                  {
                    label: 'Landlord Fees (fully managed and rent receipt)',
                    name: 'fully_managed_landlord_fees',
                    type: 'currency',
                    hint: 'All monies that are invoiced to Landlords for fully managed or rent receipt service. Exclude any monthly commissions and ignore any tenant find service monies.'
                  },
                  {
                    label: 'Landlord commission fully managed and rent receipt',
                    name: 'fully_managed_landlord_commission',
                    type: 'currency',
                    hint: 'All monthly commission invoiced to Landlords on the fully managed and rent receipt service.'
                  },
                  {
                    label: 'Landlord fees and commissions (tenant find service)',
                    name: 'tenant_find_fees_and_commission',
                    type: 'currency',
                    hint: 'All monies that are invoiced to Landlords on the tenant find (let only) service. Enter the amount invoiced to the Landlord whether a it is a fixed £s fee or a commission based on % of rent.'
                  },
                  {
                    label: 'Insurance Commission',
                    name: 'insurance_commission',
                    type: 'currency',
                    hint: 'Commissions received from insurance agreements.'
                  },
                  {
                    label: 'Other Income',
                    name: 'other_lettings_income',
                    type: 'currency',
                    hint: 'Any other income including but not limited to referral fees from other agents, referral money from any third party, commissions received from contractors and service suppliers etc.'
                  },
                  {
                    label: 'Tenant Fees',
                    name: 'tenant_fees',
                    type: 'currency',
                    hint: 'All permitted Tenant monies invoiced to Tenants including but not limited to: Tenant liability insurance, pet fees and or pet insurance, late payment charges, mutual surrender and liquidated damages, agreed remedial works charges, missed appointment charges, any other income paid by tenants permitted by the Tenant Fees Act 2019.'
                  }
                ]
              },
              {
                name: 'Landlord Clients',
                fields: [
                  {
                    label: 'Appraisals Carried Out',
                    name: 'lettings_appraisals_carried_out',
                    type: 'integer',
                    hint: 'Each property that has been visited / appraised during the month regardless of whether an instruction was received at the month end (Jupix - if there is an appraisal in the diary for the month then we will assume that someone attended).'
                  },
                  {
                    label: 'Appraisals Won',
                    name: 'lettings_appraisals_won',
                    type: 'integer',
                    hint: 'Each property that has been visited / appraised at some point previously and an instruction has been received within the reporting month and evidenced either by written terms of business being signed or marketing commenced and written terms are in transit.'
                  }
                ]
              },
              {
                name: 'Tenant Applications',
                fields: [
                  {
                    label: 'Viewings Conducted',
                    name: 'lettings_viewings_conducted',
                    type: 'integer',
                    hint: 'Every viewing counts i.e. 1 applicant views 6 properties then this counts as 6 viewings (not 1) (Jupix - if there is a viewing in the diary for the month then we will assume that someone attended).'
                  },
                  {
                    label: 'Applicants Pending',
                    name: 'lettings_applicants_pending',
                    type: 'integer',
                    hint: 'Applicants who have made an application to take a property in the reporting month whether they have proceeded or not, whether the tenancy has already started, or will start after the month end, or whether the applicant has been rejected (Jupix - the applicant will have been through an application pending status at a point).'
                  }
                ]
              }
            ],
            [
              {
                name: 'All Properties',
                fields: [
                  {
                    label: 'To Let Properties',
                    name: 'lettings_properties_to_let',
                    type: 'integer',
                    hint: 'The total number of properties on the market to let at the end of the month regardless of service level, where the property is in lettable condition, an earliest date by which the property will be ready to occupy is established, and the current tenants (if any) are subject to or have given, notice to vacate.'
                  },
                  {
                    label: 'Properties Withdrawn',
                    name: 'lettings_properties_withdrawn',
                    type: 'integer',
                    hint: 'Properties that have been instructions, regardless of service level, which have been withdrawn and not let. Will include let or sold by another agent, landlord lets or re-occupies or if the property is sold by us with vacant possession. (Jupix - we have a way of seeing exactly which properties in Jupix have been dis-instructed and removed from the portfolio).'
                  },
                  {
                    label: 'Lets in Pipeline',
                    name: 'lets_in_pipeline',
                    type: 'integer',
                    hint: 'All applicants who have passed referencing at any time and are waiting to move in during a future month (Jupix - i.e. the move in date is post the reporting month and has previously been through the applicant pending status).'
                  }
                ]
              },
              {
                name: 'Fully Managed & Rent Receipt',
                fields: [
                  {
                    label: 'First lets, never been let before',
                    name: 'fully_managed_first_time_lets',
                    type: 'integer',
                    hint: 'Properties that have been instructed on a fully managed or rent receipt service that have never previously been let by this office and within the reporting month an applicant has become a tenant at this property.'
                  },
                  {
                    label: 'Relets',
                    name: 'fully_managed_relets',
                    type: 'integer',
                    hint: 'Properties that have been instructed on a fully managed or rent receipt service that have previously been let by this office and within the reporting month an applicant has become a tenant at this property.'
                  },
                  {
                    label: 'Tenanted Portfolio',
                    name: 'fully_managed_tenanted_portfolio',
                    type: 'integer',
                    hint: 'All fully managed and rent receipt live tenancies (including periodic) with a tenancy end date beyond the reporting month end date.'
                  },
                  {
                    label: 'Active Periodic Tenancies',
                    name: 'active_periodic_tenancies',
                    type: 'integer',
                    hint: 'All fully managed and rent receipt live tenancies occupied on a periodic basis.'
                  }
                ]
              },
              {
                name: 'Tenant Find Tenancies',
                fields: [
                  {
                    label: 'First lets, never been let before',
                    name: 'tenant_find_first_time_lets',
                    type: 'integer',
                    hint: 'Properties that have been instructed on a tenant find only service that have never previously been let by this office and within the reporting month an applicant has become a tenant at this property.'
                  },
                  {
                    label: 'Relets',
                    name: 'tenant_find_relets',
                    type: 'integer',
                    hint: 'Properties that have been instructed on a tenant find only service that have previously been let by this office and within the reporting month an applicant has become a tenant at this property.'
                  }
                ]
              }
            ]
          ]
        },
        {
          name: 'Sales & Financial Services',
          type: 'form',
          columns: [
            [
              {
                name: 'Property Stock',
                fields: [
                  {
                    label: 'Valuations Carried Out',
                    name: 'sales_valuations_carried_out',
                    type: 'integer',
                    hint: 'Each property that has been visited / appraised during the month regardless of whether an instruction was received at the month end (including the on-line service).'
                  },
                  {
                    label: 'Listings Won',
                    name: 'sales_listings_won',
                    type: 'integer',
                    hint: 'Each property that has been visited / appraised at some point previously and an instruction has been received within the reporting month and evidenced either by written agency agreement signed or marketing commenced and written terms are in transit (including the on-line service).'
                  },
                  {
                    label: 'Number of sold properties in pipeline',
                    name: 'sales_in_pipeline',
                    type: 'integer',
                    hint: 'Number of properties sold S.T.C. and awaiting exchange at the end of the reporting month.'
                  },
                  {
                    label: 'Sales In Pipeline Fees',
                    name: 'sales_in_pipeline_fees',
                    type: 'currency',
                    hint: 'The total fees expected to be achieved from the sales agreed sat in the pipeline if they all proceed to exchange and completion.'
                  },
                  {
                    label: 'Sales Agreed (number)',
                    name: 'sales_agreed',
                    type: 'integer',
                    hint: 'Number of properties where a sale has been agreed in the reporting month.'
                  },
                  {
                    label: 'Sales Agreed Fees',
                    name: 'sales_agreed_fees',
                    type: 'currency',
                    hint: 'Total fee value of gross sales agreed in the month'
                  },
                  {
                    label: 'Sales Fallen Through',
                    name: 'sales_fallen_through',
                    type: 'integer',
                    hint: 'The total number of sales that have fallen through within the reporting month to include any exchanged sales that have fallen through.'
                  },
                  {
                    label: 'Sales Fallen Through Fees',
                    name: 'sales_fallen_through_fees',
                    type: 'currency',
                    hint: 'The total potential fees lost through sales that have fallen through within the reporting period.'
                  },
                  {
                    label: 'Sales Exchanged (number)',
                    name: 'sales_exchanged',
                    type: 'integer',
                    hint: 'Number of properties where contracts have been exchanged in the reporting month.'
                  },
                  {
                    label: 'Sales Exchanged Total Price',
                    name: 'sales_exchanged_total_price',
                    type: 'currency',
                    hint: 'Total prices of contracts exchanged properties in the month'
                  },
                  {
                    label: 'Sales Completed (number)',
                    name: 'sales_completed',
                    type: 'integer',
                    hint: 'The total number of sales completed within the reporting month.'
                  },
                  {
                    label: 'Sole Agency Instructions',
                    name: 'sole_agency_instructions',
                    type: 'integer',
                    hint: 'Number of new sales listings i.e. Listings won, but only those which are sole agency'
                  },
                  {
                    label: 'Number of Properties Fully Available',
                    name: 'sales_properties_available',
                    type: 'integer',
                    hint: 'Number of unsold properties'
                  },
                  {
                    label: 'Total Asking Price of Instructions',
                    name: 'sales_total_asking_price',
                    type: 'currency',
                    hint: 'Total prices of unsold properties at the end of the month'
                  },
                  {
                    label: 'Total Commission of Instructions',
                    name: 'sales_total_commission',
                    type: 'currency',
                    hint: 'Total fee value of unsold properties at the end of the month'
                  },
                  {
                    label: 'Number of Price Reductions > 5%',
                    name: 'sales_price_reductions',
                    type: 'integer',
                    hint: 'Total number of price reductions which exceed 5% of the property value'
                  },
                  {
                    label: 'Land and New Homes Exchange Fees',
                    name: 'land_and_new_homes_exchange_fees',
                    type: 'currency',
                    hint: 'Total fee value of contracts exchanged on land and new homes properties'
                  },
                  {
                    label: 'Withdrawals',
                    name: 'sales_withdrawals',
                    type: 'integer',
                    hint: 'Numbers of properties witrhdrawn from sale prior to sale agreed'
                  }
                ]
              }
            ],
            [
              {
                name: 'Sales Income',
                fields: [
                  {
                    label: 'Sales Exchanged Fees',
                    name: 'sales_exchanged_fees',
                    type: 'currency',
                    hint: 'Monetary value of sales exchanged fees for those properties that have exchanged within the reporting month.'
                  },
                  {
                    label: 'Sales Completed Fees',
                    name: 'sales_completed_fees',
                    type: 'currency',
                    hint: 'Monetary value of all sales fees completed within the reporting month.'
                  },
                  {
                    label: 'Online fees total',
                    name: 'online_fees_total',
                    type: 'currency',
                    hint: 'Online fees total'
                  },
                  {
                    label: 'Added Extras Income',
                    name: 'added_extras_income',
                    type: 'currency',
                    hint: 'Added extras income'
                  }
                ]
              },
              {
                name: 'Financial Services',
                fields: [
                  {
                    label: 'Total amount earned from mortgage commissions',
                    name: 'mortgage_commission',
                    type: 'currency',
                    hint: 'All monetary commissions received from mortgage referrals.'
                  },
                  {
                    label: 'Number of mortgages completions',
                    name: 'mortgage_completions',
                    type: 'integer',
                    hint: 'The total number of mortgage referrals on which commission was received.'
                  },
                  {
                    label: 'Total amount earned from life insurance commissions',
                    name: 'life_insurance_commission',
                    type: 'currency',
                    hint: 'All monetary commissions received from life insurance referrals.'
                  },
                  {
                    label: 'Number of life policies placed',
                    name: 'life_policies_placed',
                    type: 'integer',
                    hint: 'The total number of life insurance referrals on which commission was received.'
                  }
                ]
              },
              {
                name: 'Conveyancing',
                fields: [
                  {
                    label: 'Total amount earned from conveyancing commissions',
                    name: 'conveyancing_commission',
                    type: 'currency',
                    hint: 'All monetary commissions received from conveyancing referrals.'
                  },
                  {
                    label: 'Number of conveyancing completions',
                    name: 'conveyancing_completions',
                    type: 'integer',
                    hint: 'The total number of conveyancing cases on which commission was received.'
                  }
                ]
              },
              {
                name: 'Applicants',
                fields: [
                  {
                    label: 'Number of Viewings',
                    name: 'sales_total_viewings',
                    type: 'integer',
                    hint: 'Number of occasions a potential buyer has seen a property accompanied by a member of staff'
                  },
                  {
                    label: 'Number of New Applicants',
                    name: 'sales_new_applicants',
                    type: 'integer',
                    hint: 'The total number of new applicants registered during the reporting period.'
                  }
                ]
              }
            ]
          ]
        },
        {
          name: 'Billable Items',
          type: 'billable_items'
        },
        {
          name: 'Staff List',
          type: 'staff'
        },
        {
          name: 'Totals',
          type: 'totals'
        }
      ],
      errorBag: new ErrorBag()
    }
  },
  computed: {
    ...mapState(['selectedOffice']),
    filteredTabs () {
      return this.tabs.filter((item) => {
        let result = true
        if (!this.officeInfo) {
          result = result && false
        }
        if (!this.officeInfo.has_lettings_department) {
          result = result && (item.name !== 'Lettings')
        }
        if (!this.officeInfo.has_sales_department) {
          result = result && (item.name !== 'Sales & Financial Services')
        }
        return result
      })
    },
    totalsTab () {
      const totalLettings = parseFloat(this.form.tenant_fees || 0) +
        parseFloat(this.form.fully_managed_landlord_fees || 0) +
        parseFloat(this.form.fully_managed_landlord_commission || 0) +
        parseFloat(this.form.tenant_find_fees_and_commission || 0) +
        parseFloat(this.form.insurance_commission || 0) +
        parseFloat(this.form.other_lettings_income || 0)

      const totalSales = parseFloat(this.form[this.officeInfo.network.msf_sale_income_id === 1 ? 'sales_exchanged_fees' : 'sales_completed_fees'] || 0) +
        parseFloat(this.form.online_fees_total || 0) +
        parseFloat(this.form.added_extras_income || 0)

      const totalFinancialServices = parseFloat(this.form.mortgage_commission || 0) +
        parseFloat(this.form.life_insurance_commission || 0) +
        parseFloat(this.form.conveyancing_commission || 0)

      const totalIncome = totalLettings + totalSales + totalFinancialServices

      let managedServiceFeeSales = 0
      let managedServiceFeeSalesVat = 0
      let managedServiceFeeLettings = 0
      let managedServiceFeeLettingsVat = 0
      let managedServiceFeeTotal = 0
      let managedServiceFeeTotalVat = 0
      let nationalMarketingFund = 0
      let nationalMarketingFundVat = 0

      if (this.form.fee.pays_msf) {
        if (this.form.fee.msf_split_fees) {
          if (this.form.fee.msf_sales_value) {
            managedServiceFeeSales = this.form.fee.msf_sales_type_id === 1
              ? this.form.fee.msf_sales_value : Math.max(this.form.fee.msf_sales_threshold, (totalSales + totalFinancialServices) * (this.form.fee.msf_sales_value / 100))
            managedServiceFeeSales = parseFloat(MathUtils.roundTo(managedServiceFeeSales, 2))
            managedServiceFeeSalesVat = parseFloat(MathUtils.roundTo((managedServiceFeeSales * 0.2), 2))
          }

          if (this.form.fee.msf_lettings_value) {
            managedServiceFeeLettings = this.form.fee.msf_lettings_type_id === 1 ? this.form.fee.msf_lettings_value
              : (this.form.fee.msf_subtract_lets ? 0 : Math.max(this.form.fee.msf_lettings_threshold, totalLettings * (this.form.fee.msf_lettings_value / 100)))
            managedServiceFeeLettings = parseFloat(MathUtils.roundTo(managedServiceFeeLettings, 2))
            managedServiceFeeLettingsVat = parseFloat(MathUtils.roundTo((managedServiceFeeLettings * 0.2), 2))
          }

          managedServiceFeeTotal = managedServiceFeeSales + managedServiceFeeLettings
          managedServiceFeeTotalVat = managedServiceFeeSalesVat + managedServiceFeeLettingsVat
        } else {
          managedServiceFeeTotal = this.form.fee.msf_type_id === 1 ? this.form.fee.msf_value
            : Math.max(this.form.fee.msf_threshold, totalIncome * (this.form.fee.msf_value / 100)) - (this.form.fee.msf_subtract_lets ? totalLettings : 0) * (this.form.fee.msf_value / 100)
          managedServiceFeeTotal = parseFloat(MathUtils.roundTo(managedServiceFeeTotal, 2))
          managedServiceFeeTotalVat = parseFloat(MathUtils.roundTo((managedServiceFeeTotal * 0.2), 2))
        }
      }

      if (this.form.fee.pays_nmf) {
        nationalMarketingFund = this.form.fee.nmf_type_id === 1 ? this.form.fee.nmf_value : Math.max(this.form.fee.nmf_threshold, (totalIncome - (this.form.fee.msf_subtract_lets ? totalLettings : 0)) * (this.form.fee.nmf_value / 100))
        nationalMarketingFund = parseFloat(MathUtils.roundTo(nationalMarketingFund, 2))
        nationalMarketingFundVat = parseFloat(MathUtils.roundTo((nationalMarketingFund * 0.2), 2))
      }

      const billableItems = this.form.billable_items
      const billableItemsVat = this.form.billable_items_vat
      const totalPayable = managedServiceFeeTotal + managedServiceFeeTotalVat + nationalMarketingFund + nationalMarketingFundVat + billableItems + billableItemsVat

      const startFields = [
        {
          label: 'Total Income Lettings',
          value: totalLettings.toFixed(2)
        },
        {
          label: 'Total Income Sales',
          value: totalSales.toFixed(2)
        },
        {
          label: 'Total Income Financial Services',
          value: totalFinancialServices.toFixed(2)
        },
        {
          label: 'Total Income',
          value: totalIncome.toFixed(2)
        }
      ]
      const msfFields = this.form.fee.msf_split_fees ? [
        {
          label: 'Management Service Fee Sales',
          value: managedServiceFeeSales.toFixed(2)
        },
        {
          label: 'Management Service Fee Sales VAT',
          value: managedServiceFeeSalesVat.toFixed(2)
        },
        {
          label: 'Management Service Fee Lettings',
          value: managedServiceFeeLettings.toFixed(2)
        },
        {
          label: 'Management Service Fee Lettings VAT',
          value: managedServiceFeeLettingsVat.toFixed(2)
        },
        {
          label: 'Management Service Fee Total',
          value: managedServiceFeeTotal.toFixed(2)
        },
        {
          label: 'Management Service Fee Total VAT',
          value: managedServiceFeeTotalVat.toFixed(2)
        }
      ] : [
        {
          label: 'Management Service Fee',
          value: managedServiceFeeTotal.toFixed(2)
        },
        {
          label: 'Management Service Fee VAT',
          value: managedServiceFeeTotalVat.toFixed(2)
        }
      ]
      const endFields = [
        {
          label: this.officeInfo.network.nmf_label,
          value: nationalMarketingFund.toFixed(2)
        },
        {
          label: `${this.officeInfo.network.nmf_label} VAT`,
          value: nationalMarketingFundVat.toFixed(2)
        },
        {
          label: 'Billable Items',
          value: billableItems.toFixed(2)
        },
        {
          label: 'Billable Items VAT',
          value: billableItemsVat.toFixed(2)
        },
        {
          label: 'Total Payment Due',
          value: totalPayable.toFixed(2)
        }
      ]

      return [
        {
          name: 'Totals',
          fields: startFields.concat(msfFields).concat(endFields)
        }
      ]
    },
    totalFields () {
      let total = 0
      for (let t = 0; t < this.filteredTabs.length; t++) {
        if (this.filteredTabs[t].type !== 'form') {
          continue
        }

        for (let c = 0; c < this.filteredTabs[t].columns.length; c++) {
          for (let b = 0; b < this.filteredTabs[t].columns[c].length; b++) {
            total += this.filteredTabs[t].columns[c][b].fields.length
          }
        }
      }

      return total
    },
    completedFields () {
      let total = 0
      for (let t = 0; t < this.filteredTabs.length; t++) {
        if (this.filteredTabs[t].type !== 'form') {
          continue
        }

        for (let c = 0; c < this.filteredTabs[t].columns.length; c++) {
          for (let b = 0; b < this.filteredTabs[t].columns[c].length; b++) {
            for (let f = 0; f < this.filteredTabs[t].columns[c][b].fields.length; f++) {
              if (this.form[this.filteredTabs[t].columns[c][b].fields[f].name] !== null && this.form[this.filteredTabs[t].columns[c][b].fields[f].name] !== '') {
                total++
              }
            }
          }
        }
      }

      return total
    }
  },
  created () {
    this.getReport()
    this.getUsers()
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    getReport () {
      v2Api.get(`managed-service-fee-reports/${this.id}`).then((response) => {
        this.billableItems = response.data.billable_item_instances
        this.officeInfo = response.data.office
        delete response.data.billable_item_instances
        delete response.data.office

        // Enforce 2dp
        for (let t = 0; t < this.filteredTabs.length; t++) {
          if (!this.filteredTabs[t].columns) {
            continue
          }

          for (let c = 0; c < this.filteredTabs[t].columns.length; c++) {
            for (let b = 0; b < this.filteredTabs[t].columns[c].length; b++) {
              for (let f = 0; f < this.filteredTabs[t].columns[c][b].fields.length; f++) {
                const type = this.filteredTabs[t].columns[c][b].fields[f].type
                const name = this.filteredTabs[t].columns[c][b].fields[f].name
                if (type !== 'currency' || response.data[name] === null) {
                  continue
                }

                response.data[name] = parseFloat(response.data[name]).toFixed(2)
              }
            }
          }
        }

        this.form = response.data
      })
    },
    getUsers () {
      api
        .get('/users', {
          params: { office_id: this.selectedOffice.id }
        })
        .then(response => {
          this.users = response.data
        })
    },
    currencyOnly (field) {
      let val = this.form[field]
      if (typeof val === 'string') {
        val = this.form[field].replace(/[^\d.]/g, '')
      }
      if (!val) {
        val = '0.00'
      } else {
        val = parseFloat(val).toFixed(2)
      }

      this.form[field] = val
    },
    numbersOnly (field) {
      let val = this.form[field]
      if (typeof val === 'string') {
        val = this.form[field].replace(/[^\d.]/g, '')
      }
      if (!val) {
        val = 0
      } else {
        val = parseFloat(val)
      }

      this.form[field] = val
    },
    save () {
      this.saving = true
      this.errorBag.clear()

      v2Api.patch(`managed-service-fee-reports/${this.id}`, this.form).then((response) => {
        this.form.status_id = response.data.status_id
        this.displayToastNotification({
          type: this.completedFields < this.totalFields ? 'warning' : 'success',
          icon: this.completedFields < this.totalFields ? 'fa-bell' : 'fa-check-circle',
          title: 'Thank You',
          info: this.completedFields < this.totalFields ? 'PLEASE NOTE: Your report has been saved but not submitted. You need to enter figures in all fields in order to submit.' : 'The report has been submitted.'
        })
        this.saving = false
      }, (errors) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: errors.response.data.message
        })
        this.errorBag.store(errors.response.data)
        this.saving = false
      })
    },
    openUrl (url) {
      window.open(url, '_blank')
    }
  }
}
</script>

<style scoped>
.active::after {
  border-left: 15px solid transparent;
  border-right: 15px solid transparent;
  border-top: 15px solid #fff;
    border-top-color: rgb(255, 255, 255);
  content: "";
  left: 50%;
  position: absolute;
  top: 100%;
  z-index: 1;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  border-top-color: inherit;
}
</style>
